import React, { useEffect } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';
import { isEmpty } from 'lodash';

import lang from '../../../../lib/language';
import { Week52HighLowResponse } from '../../../../lib/models/fundamental/types';
import { RootState } from '../../../../lib/store';
import { FundamentalCall } from '../../../../lib/store/fundamental/types';
import { getLimitedText } from '../../../../lib/util/DataHelpers';
import { isCallStatusReady } from '../../../../lib/util/error-handling/StatusByCallHelpers';
import { manageSubscriptions } from '../../../../lib/util/TradingHelpers';

import { BaseTable, CompanyLogo } from '../../../../components';
import ClickableDiv from '../../../../components/ClickableDiv/ClickableDiv';
import NoDataAvailable from '../../../../components/NoDataAvailable/NoDataAvailable';
import { useIsMobile } from '../../../../hooks';
import { UI_ROUTES } from '../../../../util/routes';
import {
  WEEK52_GAINERS_AND_LOSERS_SYMBOL_NAME_LIMIT,
  WEEK52_GAINERS_AND_LOSERS_SYMBOL_NAME_MOB_LIMIT,
  WEEK52_GAINERS_AND_LOSERS_TABLE_HEADERS,
  WEEK52_HIGH_LOW_HEADERS_MOBILE,
} from '../../Week52GainersAndLosers.constants';
import PriceAndChange from '../PriceAndChange/PriceAndChange';

import styles from '../../Week52GainersAndLosers.module.scss';

type OwnProps = { data: Week52HighLowResponse[], title: string, isReduction: boolean, isTopGainersSelected: boolean}
type Props = OwnProps & ReduxProps;

const Week52GainersAndLosersList = (props: Props) => {
  const { companyLogoAndName, isWeek52HighLowTopListReady, data, isReduction, title, isTopGainersSelected } = props;
  const isReadyAndNoData = isWeek52HighLowTopListReady && isEmpty(data);
  const isMobile = useIsMobile();
  const history = useHistory();
  const tableHeaders1 = isMobile ? ['', '', ''] : [title, '', ''].map((item: string) => <th key={item}>{item}</th>);
  const tableHeaders2 = (
    isMobile ? WEEK52_HIGH_LOW_HEADERS_MOBILE : WEEK52_GAINERS_AND_LOSERS_TABLE_HEADERS
  ).map((item: string) => (
    <th
      className={classNames({
        [styles.headerTh]: !isMobile,
      })}
      key={item}
    >
      {item}
    </th>
  ));

  const SkeletonLoaderRow = (
    <tr>
      <td>
        <div className={styles.wrapperCompany}>
          <div className={styles.loaderLogo} />
          <div className={styles.companyInfo}>
            <div className={classNames(styles.loader, styles.loaderMargin)} style={{ width: 48 }} />
            <div className={styles.loader} style={{ width: isMobile ? 80 : 136 }} />
          </div>
        </div>
      </td>
      <td>
        <div className={styles.loader} style={{ width: isMobile ? 48 : 68 }} />
      </td>
      <td>
        <div className={styles.wrapperRow}>
          <div>
            <div className={classNames(styles.loader, styles.loaderMargin)} style={{ width: 60 }} />
            <div className={styles.loader} style={{ width: 60 }} />
          </div>
        </div>
      </td>
    </tr>
  );

  const renderList = () => {
    const rows = data.map((item: Week52HighLowResponse): JSX.Element => {
      const logo = item.logoBase64 ?? companyLogoAndName[item.symbol]?.logo;
      const url = companyLogoAndName[item.symbol]?.url ?? item.url;
      const displayName = getLimitedText(
        companyLogoAndName[item.symbol]?.name,
        isMobile ? WEEK52_GAINERS_AND_LOSERS_SYMBOL_NAME_MOB_LIMIT : WEEK52_GAINERS_AND_LOSERS_SYMBOL_NAME_LIMIT,
      );

      return (
        <tr
          className={styles.rowWrapper}
          key={item.symbol}
          onClick={() => history.push(`${UI_ROUTES.symbol}/${item.symbol}`)}
        >
          <td>
            <div className={styles.wrapperCompany}>
              {(url || logo) && (
                <CompanyLogo url={url} logo={logo} symbol={item.symbol} />
              )}
              <div className={styles.companyInfo}>
                <p className={styles.symbol}>{item.symbol}</p>
                <p className={classNames(styles.symbol, styles.name)}>{displayName}</p>
              </div>
            </div>
          </td>
          <PriceAndChange item={item} />
        </tr>
      );
    });

    while (rows.length < 4) {
      rows.push(
        <div className={styles.row} />,
      );
    }

    return rows;
  };

  useEffect(() => {
    if (isWeek52HighLowTopListReady) {
      manageSubscriptions('discover/week52-gainers-losers', null, false);
    }
  }, [isWeek52HighLowTopListReady]);

  return (
    <>
      <BaseTable
        className={classNames(styles.table, {
          [styles.tableMobile]: isMobile,
        })}
      >
        {
          isReadyAndNoData ? (
            <NoDataAvailable
              className={styles.noDataToDisplay}
              isNoDataIcon
              title={lang.commonDiscover52WeekNoData()}
            />
          ) : (
            <>
              <thead>
                <tr className={styles.tableMainHeader}>{tableHeaders1}</tr>
                <tr>{tableHeaders2}</tr>
              </thead>
              <tbody>
                {data.length === 0 ? (
                  <>
                    {SkeletonLoaderRow}
                    {SkeletonLoaderRow}
                    {SkeletonLoaderRow}
                    {SkeletonLoaderRow}
                  </>
                ) : (
                  <>
                    {renderList()}
                  </>
                )}
              </tbody>
            </>
          )
        }
      </BaseTable>
      {
        !isReadyAndNoData && isMobile && (
          <ClickableDiv
            className={styles.viewAllBtnMob}
            onClick={() => {
              history.push({
                pathname: UI_ROUTES.discover + UI_ROUTES.fiftyTwoWeekHighLow,
                state: { isTopGainersSelected },
              });
              window.scrollTo(0, 0);
            }}
          >
            {lang.mobileFundsScreenViewAllBtn()}
          </ClickableDiv>
        )
      }
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  isWeek52HighLowTopListReady: isCallStatusReady(
    FundamentalCall.getWeek52HighLowTopList,
    state.fundamental.statusByCall,
  ),
  companyLogoAndName: state.fundamental.companyLogoAndName,
});

const connector = connect(mapStateToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(Week52GainersAndLosersList);
